<template>
    <div v-if="form.isDirty" class="bg-yellow-100 my-4 p-3 flex rounded">
        <div><ExclamationIcon class="mr-2 h-5 w-5 text-yellow-800" /></div>
        <div>
            <h3 class="text-sm font-bold text-yellow-800">
                {{ $t("actions.notSavedChanges") }}
            </h3>
            <div class="mt-2 text-sm text-yellow-700">
                <p>
                    {{ $t("actions.notSavedChangesDesc") }}
                </p>
            </div>
        </div>
    </div>

    <div class="flex justify-between my-4">
        <div v-if="applyLink">
            <secondary-button
                type="button"
                :disabled="form.isDirty"
                :title="form.isDirty ? $t('actions.saveFirst') : null"
                @click="$inertia.visitInModal(applyLink)"
            >
                <TemplateIcon class="md:mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:inline font-bold">{{
                    $t("topics.templates")
                }}</span>
            </secondary-button>
        </div>
    </div>

    <div v-if="showChangeHint" class="rounded-md bg-yellow-50 p-4 my-4">
        <div class="flex">
            <div class="flex-shrink-0">
                <ExclamationIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div class="ml-3">
                <h3 class="text-sm font-medium text-yellow-800">
                    {{ $t("topics.note") }}
                </h3>
                <div class="mt-2 text-sm text-yellow-700">
                    <p>
                        {{ $t("topics.noteText") }}
                    </p>
                </div>
            </div>
        </div>
    </div>

    <form ref="topicsForm" @submit.prevent="save">
        <topics-action-bar
            :processing="form.processing"
            @back="goBack()"
            @add="addTopic()"
        />

        <SlickList axis="y" v-model:list="form.topics" use-drag-handle>
            <SlickItem
                class="border border-gray-200 mt-3 mb-3 p-2 transition-colors duration-700"
                :class="{ 'bg-yellow-50': highlighted === topic.uuid }"
                v-for="(topic, i) in form.topics"
                :key="topic.uuid"
                :index="i"
            >
                <div
                    v-if="topic.action !== 'remove'"
                    class="md:grid grid-cols-12 gap-4 my-4"
                >
                    <div class="col-span-1 flex items-center">
                        <DragHandle
                            class="pt-4 mr-6 hover:text-slate-400 focus:text-slate-400 active:text-slate-400"
                        />
                    </div>

                    <div class="col-span-1">
                        <label
                            for="icon"
                            class="block text-sm font-medium text-slate-700"
                            >{{ $t("topics.icon") }}</label
                        >
                        <button type="button">
                            <i
                                :class="[
                                    'mr-4 h-5 w-5 mt-2 inline-block text-slate-600 hover:text-slate-500',
                                    topic.icon,
                                ]"
                                @click="editIcons(i)"
                            >
                                <PlusCircleIcon
                                    class="h-5 w-5 text-yellow-500 hover:text-yellow-400"
                                    v-if="!form.topics[i].icon"
                                />
                            </i>
                        </button>
                    </div>

                    <div
                        :class="[
                            !isTemplate
                                ? 'col-span-5 xl:col-span-2'
                                : 'col-span-10 xl:col-span-5',
                        ]"
                    >
                        <label
                            for="name"
                            class="block text-sm font-medium text-slate-700"
                            >{{ $t("topics.name") }}</label
                        >
                        <input
                            type="text"
                            name="name"
                            id="name"
                            :data-topic-name="topic.uuid"
                            v-model="form.topics[i].name"
                            class="mt-1 block w-full sm:text-sm border-gray-300 focus:ring-minplan-blue focus:border-minplan-blue focus-visible:ring-minplan-blue"
                        />
                        <jet-input-error
                            :message="form.errors[`topics.${i}.name`]"
                            class="mt-2"
                            data-field-error
                        />
                    </div>

                    <div class="col-span-5 xl:col-span-2" v-if="showBudget">
                        <label
                            for="budget"
                            class="block text-sm font-medium text-slate-700"
                            >{{ $t("topics.budget") }}</label
                        >
                        <div class="mt-1 flex">
                            <span
                                class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                                >CHF</span
                            >
                            <input
                                type="number"
                                name="budget"
                                id="budget"
                                step=".01"
                                v-model="form.topics[i].budget"
                                class="flex-1 focus:ring-minplan-blue focus:border-minplan-blue focus-visible:ring-minplan-blue focus-visible:border-minplan-blue block w-full min-w-0 sm:text-sm border-gray-300 text-right"
                            />
                        </div>
                        <jet-input-error
                            :message="form.errors[`topics.${i}.budget`]"
                            class="mt-2"
                            data-field-error
                        />
                    </div>

                    <div class="col-span-4 xl:col-span-2" v-if="showDeadlines">
                        <label
                            for="deadline"
                            class="block text-sm font-medium text-slate-700"
                            >{{ $t("topics.deadline") }}</label
                        >
                        <div class="mt-1 flex minplan-std-inputheight">
                            <span
                                class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm"
                            >
                                <CalendarIcon
                                    class="h-5 w-5 text-minplan-darkgreybeige"
                                    id="deadline"
                                />
                            </span>
                            <Calendar
                                v-model="form.topics[i].deadline"
                                dateFormat="dd.mm.yy"
                                :manual-input="true"
                                class="focus:ring-minplan-blue focus:border-minplan-blue"
                            />
                        </div>

                        <jet-input-error
                            :message="form.errors[`topics.${i}.deadline`]"
                            class="mt-2"
                            data-field-error
                        />
                    </div>

                    <div class="col-span-4 xl:col-span-2">
                        <label
                            for="suppliers"
                            class="block text-sm font-medium text-slate-700"
                            >{{ $t("topics.supplier") }}</label
                        >
                        <div class="mt-1 flex">
                            <Dropdown
                                id="suppliers"
                                v-model="form.topics[i].supplier_uuid"
                                :options="suppliers"
                                :show-clear="true"
                                optionValue="uuid"
                                optionLabel="name"
                                :placeholder="$t('suppliers.noSupplier')"
                                class="w-full"
                            />
                        </div>
                        <jet-input-error
                            :message="form.errors[`topics.${i}.supplier_uuid`]"
                            class="mt-2"
                            data-field-error
                        />
                    </div>

                    <div
                        class="col-span-4 col-end-13 xl:col-span-2 xl:col-end-auto flex items-center justify-end"
                    >
                        <button type="button">
                            <ColorSwatchIcon
                                class="mr-4 h-5 w-5 mt-6 text-minplan-darkgreybeige hover:text-slate-500"
                                @click="editOptions(i)"
                            />
                        </button>

                        <button type="button">
                            <AnnotationIcon
                                @click="editDescription(i)"
                                class="mr-4 h-5 w-5 mt-6 text-minplan-darkgreybeige hover:text-slate-500"
                            />
                        </button>

                        <button type="button">
                            <CreditCardIcon
                                @click="editInclusiveBudget(i)"
                                class="mr-4 h-5 w-5 mt-6 text-minplan-darkgreybeige hover:text-slate-500"
                            />
                        </button>

                        <button type="button">
                            <TrashIcon
                                class="h-5 w-5 mt-6 text-gray-400 hover:text-red-500"
                                @click="removeTopic(i)"
                            />
                        </button>
                    </div>

                    <!-- 2nd ROW -->
                    <div class="col-span-12">
                        <div class="md:grid grid-cols-12 gap-4">
                            <div class="col-span-4">
                                <!-- DISABLE CALCULATION SWITCH -->
                                <switch-input
                                    class="items-center justify-start"
                                    :label="$t('topics.disable_calculation')"
                                    v-model="form.topics[i].disable_calculation"
                                >
                                </switch-input>
                            </div>
                            <div class="col-span-6">
                                <!-- INCLUSIVE BUDGET -->
                                <span
                                    v-if="form.topics[i].inclusive_budget"
                                    class="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold leading-5 text-blue-800 mr-2"
                                >
                                    <CreditCardIcon class="mr-4 h-5 w-5 text-blue-900" />
                                    {{
                                        $filters.currencyCHF(
                                            form.topics[i].inclusive_budget
                                        )
                                    }}
                                    <span
                                        v-if="
                                            form.topics[i].inclusive_reference &&
                                            form.topics[i].inclusive_unit
                                        "
                                        class="text-blue-800/50 ml-2"
                                    >
                                        &#126;
                                        {{
                                            $filters.currencyCHF(
                                                form.topics[i].inclusive_budget /
                                                    form.topics[i].inclusive_reference
                                            )
                                        }}
                                        / {{ form.topics[i].inclusive_unit }}</span
                                    >
                                </span>
                                <!-- OPTIONS -->
                                <span
                                    v-for="name in setNames(i)"
                                    :key="name.i"
                                    class="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold leading-5 text-green-800"
                                >
                                    <ColorSwatchIcon
                                        class="mr-4 h-5 w-5 text-green-900"
                                    />{{ name }}
                                </span>
                            </div>
                            <div class="col-span-2">
                                <!-- ACTIVE SWITCH -->
                                <switch-input
                                    class="items-center justify-end mr-0"
                                    :label="$t('topics.active')"
                                    v-model="form.topics[i].active"
                                >
                                </switch-input>
                            </div>
                        </div>
                    </div>
                </div>
            </SlickItem>
        </SlickList>

        <div class="text-center" v-if="form.topics.length === 0">
            <h3 class="mt-2 text-sm font-medium text-gray-900">
                {{ $t("topics.noTopic") }}
            </h3>
            <p class="mt-1 text-sm text-gray-500">{{ $t("topics.noTopicDesc") }}</p>
        </div>

        <topics-action-bar
            class="mt-4"
            :processing="form.processing"
            @back="goBack()"
            @add="addTopic()"
        />
    </form>

    <sidebar
        :title="$t('options.assignSet')"
        @close="closeOptions()"
        :v-if="!template?.app_default"
        :open="showOptionsSidebar "
        class="z-50"
    >
        <primary-button @click="applySets()" class="bg-lime-600 hover:bg-lime-500">
            {{ $t("actions.apply") }}
        </primary-button>

        <fieldset class="space-y-5">
            <legend class="sr-only">Option-Sets</legend>
            <div
                v-for="set in activeSetList"
                :key="set.uuid"
                class="relative flex items-start"
            >
                <div class="flex items-center h-5">
                    <input
                        id="sets"
                        aria-describedby="sets-description"
                        name="sets"
                        type="checkbox"
                        :checked="set.selected"
                        @input="set.selected = $event.target.checked"
                        class="border-gray-300 rounded"
                    />
                </div>
                <div class="ml-3 text-sm">
                    <label for="sets" class="font-medium text-slate-700">{{
                        set.title
                    }}</label>
                    <p id="sets-description" class="text-gray-500">
                        {{ set.description }}
                    </p>
                    <p
                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        :class="[
                            set.state === 'active'
                                ? 'bg-lime-100 text-lime-800'
                                : 'bg-gray-100',
                            'text-gray-800',
                        ]"
                    >
                        {{ $t("states." + set.state) }}
                    </p>
                </div>
            </div>
        </fieldset>
    </sidebar>

    <dialog-modal :show="showIconsModal">
        <template #title>{{ $t("topics.editIcon") }}</template>
        <template #content>
            <RadioGroup v-model="selectedIcon" class="flex flex-wrap">
                <RadioGroupOption
                    v-for="key in availableIcons"
                    :key="key"
                    v-slot="{ checked }"
                    :value="key"
                    class="p-3 bg-slate-100 m-3 flex justify-center items-center"
                >
                    <i
                        :class="
                            checked
                                ? ['text-lime-500 h-6 w-6 -ml-1', key]
                                : ['text-gray-500 h-6 w-6 -ml-1', key]
                        "
                    ></i>
                </RadioGroupOption>
            </RadioGroup>
        </template>
        <template #footer>
            <secondary-button class="mr-3" type="button" @click="closeIcons()">{{
                $t("actions.cancel")
            }}</secondary-button>
            <primary-button type="button" @click="applyIcon()">{{
                $t("actions.save")
            }}</primary-button>
        </template>
    </dialog-modal>

    <dialog-modal :show="showDescriptionModal">
        <template #title>{{ $t("topics.editDescription") }}</template>
        <template #content>
            <label for="description" class="block text-sm font-medium text-slate-700">{{
                $t("topics.description")
            }}</label>

            <textarea
                type="text"
                name="reason"
                id="description"
                v-model="activeDescription"
                class="focus:ring-minplan-blue focus:border-minplan-blue w-full mt-3"
            ></textarea>
        </template>
        <template #footer>
            <secondary-button class="mr-3" type="button" @click="closeDescription()">{{
                $t("actions.cancel")
            }}</secondary-button>
            <primary-button type="button" @click="applyDescription()">{{
                $t("actions.save")
            }}</primary-button>
        </template>
    </dialog-modal>

    <dialog-modal :show="showInclusiveBudgetModal">
        <template #title>{{ $t("topics.editInclusiveBudget") }}</template>
        <template #content>
            <div class="rounded-md bg-blue-50 p-4 col-span-6 mb-3">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <InformationCircleIcon
                            class="h-5 w-5 text-blue-400"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                        <p class="text-sm text-blue-700">
                            {{ $t("topics.inclusiveBudgetDescription") }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6">
                    <label for="cost" class="block text-sm font-medium text-slate-700">{{
                        $t("topics.inclusive_budget")
                    }}</label>
                    <div class="flex">
                        <input
                            type="number"
                            name="inclusive_budget"
                            id="inclusive_budget"
                            v-model="activeInclusiveBudget"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <span
                            class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1"
                            >CHF</span
                        >
                    </div>
                    <jet-input-error
                        :message="form.errors.inclusive_budget"
                        class="mt-2"
                    />
                </div>

                <hr class="col-span-6" />

                <div class="col-span-4">
                    <label for="name" class="block text-sm font-medium text-slate-700">{{
                        $t("topics.inclusive_reference")
                    }}</label>
                    <input
                        type="number"
                        name="inclusive_reference"
                        id="inclusive_reference"
                        v-model="activeInclusiveReference"
                        class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                    />
                    <jet-input-error
                        :message="form.errors.inclusive_reference"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-2">
                    <label
                        for="inclusive_unit"
                        class="block text-sm font-medium text-slate-700"
                        >{{ $t("topics.inclusive_unit") }}</label
                    >
                    <Dropdown
                        v-model="activeInclusiveUnit"
                        :options="units"
                        optionLabel="name"
                        optionValue="value"
                        :showClear="false"
                        class="w-full mt-1 min-w-0 sm:text-sm"
                    >
                    </Dropdown>
                </div>
            </div>
        </template>

        <template #footer>
            <secondary-button
                class="mr-3"
                type="button"
                @click="closeInclusiveBudget()"
                >{{ $t("actions.cancel") }}</secondary-button
            >
            <primary-button type="button" @click="applyInclusiveBudget()">{{
                $t("actions.save")
            }}</primary-button>
        </template>
    </dialog-modal>

    <!-- Warnung beim Verlassen mit ungespeicherten Änderungen (Plan 2026-10-28, Task 5, E2).
         Bewusst ohne «Speichern»: in der Projektvorlage folgt auf das Speichern die Frage
         «Bestehende Wohnungen anpassen?», die soll nicht untergehen. -->
    <dialog-modal :show="pendingVisit !== null" @close="pendingVisit = null">
        <template #title>{{ $t("actions.leaveTitle") }}</template>
        <template #content>
            <p class="text-sm text-gray-700">{{ $t("actions.leaveText") }}</p>
        </template>
        <template #footer>
            <secondary-button class="mr-3" type="button" @click="leaveWithoutSaving()">
                {{ $t("actions.leaveWithoutSaving") }}
            </secondary-button>
            <primary-button ref="keepEditing" type="button" @click="pendingVisit = null">
                {{ $t("actions.keepEditing") }}
            </primary-button>
        </template>
    </dialog-modal>
</template>

<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PipedTitle from "@/Shared/Components/PipedTitle";
import { SlickItem, SlickList } from "vue-slicksort";
import DragHandle from "@/Shared/Components/DragHandle";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import {
    AnnotationIcon,
    CalendarIcon,
    ColorSwatchIcon,
    ExclamationIcon,
    TemplateIcon,
    TrashIcon,
    CreditCardIcon,
    InformationCircleIcon,
    PlusCircleIcon,
} from "@heroicons/vue/outline";
import { Inertia } from "@inertiajs/inertia";
import moment from "moment";
import Sidebar from "@/Shared/Components/Sidebar";
import DialogModal from "@/Jetstream/DialogModal";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import { units } from "@/Pages/Advisor/Option/values";
import SwitchInput from "@/Shared/Components/SwitchInput";
import TopicsActionBar from "@/Pages/Templates/Components/TopicsActionBar";

const availableIcons = [
    "icon-grundriss",
    "icon-boden",
    "icon-kueche",
    "icon-sanitaer",
    "icon-elektro",
    "icon-nasszellen",
    "icon-planung",
    "icon-platten",
    "icon-parkett",
    "icon-vorhang",
    "icon-decke",
    "icon-wand",
    "icon-maler",
    "icon-schreiner",
    "icon-sims",
    "icon-tuer",
    "icon-fenster",
    "icon-terasse",
];

export default defineComponent({
    props: {
        form: Object,
        goBackLink: String,
        saveLink: String,
        suppliers: Array,
        options: Array,
        type: String,
        applyLink: String,
        template: Object,
    },
    layout: MainLayout,
    components: {
        SecondaryButton,
        DialogModal,
        PipedTitle,
        SlickList,
        SlickItem,
        DragHandle,
        PrimaryButton,
        JetInput,
        JetInputError,
        JetLabel,
        TrashIcon,
        AnnotationIcon,
        CalendarIcon,
        ExclamationIcon,
        TemplateIcon,
        ColorSwatchIcon,
        CreditCardIcon,
        InformationCircleIcon,
        PlusCircleIcon,
        Sidebar,
        RadioGroup,
        RadioGroupOption,
        RadioGroupLabel,
        SwitchInput,
        TopicsActionBar,
    },
    data() {
        return {
            dragging: false,
            showOptionsSidebar: false,
            showIconsModal: false,
            showDescriptionModal: false,
            showInclusiveBudgetModal: false,
            activeSetList: [],
            selectedIndex: null,
            activeDescription: null,
            activeInclusiveBudget: null,
            activeInclusiveReference: null,
            activeInclusiveUnit: null,
            selectedIcon: null,
            units,
            availableIcons,
            // neu hinzugefügtes Thema kurz hervorheben
            highlighted: null,
            // abgefangener Inertia-Besuch, solange das Warnfenster offen ist
            pendingVisit: null,
            // true zwischen «Ohne Speichern verlassen» und dem Ende des wiederholten Besuchs
            leaving: false,
        };
    },
    mounted() {
        // Navigationswächter (Plan 2026-10-28, Task 5, E2): solange etwas ungespeichert ist,
        // wird JEDER Inertia-Besuch abgefangen — GET wie POST (Abmelden), auch dieselbe Seite.
        // Durch gehen nur das eigene Speichern (PUT auf genau `saveLink`) und das Nachladen
        // einzelner Props (`only`, z. B. `transfer` der Projektseite). `visitInModal` läuft
        // über Axios und erreicht den Wächter nicht; der Knopf «Vorlagen» ist deshalb selbst
        // gesperrt.
        // Bekannte Lücke (Entscheid Luca, E2): der Zurück-Knopf des BROWSERS. Inertia 0.10
        // behandelt `popstate` ohne `before`-Ereignis, dort gibt es nichts abzufangen.
        this.removeLeaveGuard = Inertia.on("before", (event) => {
            const visit = event.detail.visit;
            if (this.leaving || !this.form.isDirty) return;
            if (visit.only && visit.only.length > 0) return;
            if (this.isOwnSave(visit)) return;
            event.preventDefault();
            this.pendingVisit = visit;
            this.$nextTick(() =>
                setTimeout(() => this.$refs.keepEditing?.$el?.focus(), 50)
            );
        });
        // Tab schliessen oder neu laden: dafür genügt die Warnung des Browsers
        this.onBeforeUnload = (event) => {
            if (this.leaving || !this.form.isDirty) return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", this.onBeforeUnload);
    },
    beforeUnmount() {
        this.removeLeaveGuard?.();
        window.removeEventListener("beforeunload", this.onBeforeUnload);
        clearTimeout(this.focusTimer);
        clearTimeout(this.highlightTimer);
    },
    computed: {
        showChangeHint() {
            return this.type === "project";
        },
        showDeadlines() {
            return this.type === "apartment";
        },
        showBudget() {
            return this.type === "project" || this.type === "apartment";
        },
        isTemplate() {
            return this.type === "template";
        },
    },
    methods: {
        goBack() {
            Inertia.visit(this.goBackLink);
        },
        save() {
            // Termine nur im gesendeten Payload umwandeln, nie in der Form: nach einem
            // Validierungsfehler bleibt die Form stehen (`preserveState: "errors"`), und ein
            // zweiter Versuch läse sonst den ISO-String als "DD.MM.YYYY" ("Invalid date").
            // `transform` gilt für jeden folgenden Submit dieser Form; er wird hier bei jedem
            // Speichern neu gesetzt und ist der einzige Submit der Form.
            this.form.transform((data) => ({
                ...data,
                topics: data.topics.map((topic) => ({
                    ...topic,
                    deadline: topic.deadline
                        ? moment(topic.deadline, "DD.MM.YYYY").format()
                        : null,
                })),
            }));

            this.form.put(this.saveLink, {
                preserveScroll: true,
                preserveState: "errors",
                onError: () => this.$nextTick(() => this.revealFirstError()),
                onSuccess: () =>
                    this.$notify(
                        {
                            group: "success",
                            title: "actions.success",
                            text: "templates.updated",
                        },
                        2000
                    ),
            });
        },
        // Nach einem Validierungsfehler zum ersten Feld mit Meldung springen (Reihenfolge der
        // Seite) und den Cursor hineinsetzen — wer über die obere Leiste speichert, sähe die
        // Meldung am Listenende sonst nicht. Gesucht wird die erste sichtbare Zeilenmeldung,
        // das Feld ist das erste Eingabeelement derselben Spalte.
        revealFirstError() {
            const form = this.$refs.topicsForm;
            if (!form) return;
            const message = Array.from(form.querySelectorAll("[data-field-error]")).find(
                (el) => el.textContent.trim() !== ""
            );
            const field = message?.parentElement?.querySelector(
                "input, textarea, select"
            );
            if (!field) return;
            field.scrollIntoView({ behavior: "smooth", block: "center" });
            field.focus({ preventScroll: true });
        },
        // das eigene Speichern: PUT auf genau `saveLink`, Abfrage und Anker zählen nicht
        isOwnSave(visit) {
            if (String(visit.method).toLowerCase() !== "put" || !this.saveLink)
                return false;
            const strip = (href) => {
                const url = new URL(href, window.location.origin);
                url.search = "";
                url.hash = "";
                return url.href;
            };
            return strip(visit.url.href) === strip(this.saveLink);
        },
        // den abgefangenen Besuch so wiederholen, wie er gemeint war (Abmelden ist ein POST).
        // `leaving` fällt am Ende zurück — auch wenn der Besuch scheitert, sonst bliebe der
        // Wächter auf dieser Seite dauerhaft aus.
        leaveWithoutSaving() {
            const visit = this.pendingVisit;
            if (!visit) return;
            this.pendingVisit = null;
            this.leaving = true;
            Inertia.visit(visit.url.href, {
                method: visit.method,
                data: visit.data,
                replace: visit.replace,
                preserveScroll: visit.preserveScroll,
                preserveState: visit.preserveState,
                headers: visit.headers,
                errorBag: visit.errorBag,
                forceFormData: visit.forceFormData,
                onFinish: () => {
                    this.leaving = false;
                },
            });
        },
        addTopic() {
            const uuid = _.uniqueId();
            this.form.topics.push({
                uuid,
                name: null,
                icon: null,
                budget: 0,
                active: true,
                disable_calculation: false,
                deadline: null,
                supplier_uuid: null,
                action: "add",
                position: 0,
            });
            // Das neue Thema steht am Ende: hinspringen, kurz hervorheben, Cursor ins Feld
            // «Name» — sonst passiert beim Klick auf die obere Leiste sichtbar nichts.
            // Die Hervorhebung hält 3 s: der weiche Bildlauf dauert in langen Listen ~1.2 s.
            // Ein neuer Klick löst die Timer des vorigen ab, sonst zöge dessen Fokus-Timer
            // den Cursor nachträglich ins vorige Thema zurück.
            this.$nextTick(() => {
                const input = document.querySelector(`[data-topic-name="${uuid}"]`);
                if (!input) return;
                input.scrollIntoView({ behavior: "smooth", block: "center" });
                clearTimeout(this.focusTimer);
                clearTimeout(this.highlightTimer);
                this.focusTimer = setTimeout(
                    () => input.focus({ preventScroll: true }),
                    400
                );
                this.highlighted = uuid;
                this.highlightTimer = setTimeout(() => {
                    if (this.highlighted === uuid) this.highlighted = null;
                }, 3000);
            });
        },
        removeTopic(index) {
            if (this.form.topics[index].action === "add") {
                this.form.topics.splice(index, 1);
                return;
            }
            this.form.topics[index].action = "remove";
        },
        editOptions(index) {
            const selectedTopic = this.form.topics[index];
            this.selectedIndex = index;

            this.activeSetList = this.options
                .map((option) => {
                    return {
                        ...option,
                        selected: Array.isArray(selectedTopic.sets)
                            ? selectedTopic.sets.includes(option.uuid)
                            : true,
                    };
                })
                .filter((o) => o.state === "active");

            this.showOptionsSidebar = true;
        },
        editIcons(index) {
            this.selectedIcon = this.form.topics[index].icon;
            this.selectedIndex = index;

            this.showIconsModal = true;
        },
        closeOptions() {
            this.activeSetList = [];
            this.showOptionsSidebar = false;
            this.selectedIndex = null;
        },
        closeIcons() {
            this.selectedIcon = null;
            this.selectedIndex = null;

            this.showIconsModal = false;
        },
        setNames(index) {
            const selectedTopic = this.form.topics[index];
            const toFilter = Array.isArray(selectedTopic.sets) ? selectedTopic.sets : [];
            return this.options
                .filter((t) => toFilter.includes(t.uuid))
                .map((o) => o.title);
        },
        applySets() {
            this.form.topics[this.selectedIndex].sets = this.activeSetList
                .filter((s) => s.selected === true)
                .map((s) => s.uuid);
            this.activeSetList = [];
            this.selectedIndex = null;
            this.showOptionsSidebar = false;
        },
        applyIcon() {
            this.form.topics[this.selectedIndex].icon = this.selectedIcon;
            this.selectedIcon = null;
            this.selectedIndex = null;
            this.showIconsModal = false;
        },
        editDescription(index) {
            const selectedTopic = this.form.topics[index];
            this.selectedIndex = index;
            this.activeDescription = selectedTopic.description;
            this.showDescriptionModal = true;
        },
        editInclusiveBudget(index) {
            const selectedTopic = this.form.topics[index];
            this.selectedIndex = index;
            this.activeInclusiveBudget = selectedTopic.inclusive_budget;
            this.activeInclusiveReference = selectedTopic.inclusive_reference;
            this.activeInclusiveUnit = selectedTopic.inclusive_unit;
            this.showInclusiveBudgetModal = true;
        },
        closeDescription() {
            this.activeDescription = null;
            this.showDescriptionModal = false;
            this.selectedIndex = null;
        },
        applyDescription() {
            this.form.topics[this.selectedIndex].description = this.activeDescription;
            this.activeDescription = null;
            this.selectedIndex = null;
            this.showDescriptionModal = false;
        },
        applyInclusiveBudget() {
            this.form.topics[this.selectedIndex].inclusive_budget =
                this.activeInclusiveBudget;
            this.form.topics[this.selectedIndex].inclusive_reference =
                this.activeInclusiveReference;
            this.form.topics[this.selectedIndex].inclusive_unit =
                this.activeInclusiveUnit;
            this.activeInclusiveBudget = null;
            this.activeInclusiveReference = null;
            this.activeInclusiveUnit = null;
            this.selectedIndex = null;
            this.showInclusiveBudgetModal = false;
        },
        closeInclusiveBudget() {
            this.activeInclusiveBudget = null;
            this.activeInclusiveReference = null;
            this.activeInclusiveUnit = null;
            this.showInclusiveBudgetModal = false;
            this.selectedIndex = null;
        },
    },
});
</script>

<style scoped></style>
